import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import DesignTokens from '../../styles/DesignTokens';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import BudgetDonutChart from './BudgetDonutChart';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${DesignTokens.spacing.lg};
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: ${GlassmorphismTokens.blur.md};
    -webkit-backdrop-filter: ${GlassmorphismTokens.blur.md};
  }
`;

const PlannerCard = styled(motion.div)`
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius.xl};
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${DesignTokens.spacing.xl};
  position: relative;
  z-index: 1;
  color: white;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(30, 40, 60, 0.95);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${DesignTokens.spacing.lg};
  right: ${DesignTokens.spacing.lg};
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: ${DesignTokens.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Heading = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  margin: 0 0 ${DesignTokens.spacing.xl} 0;
  text-align: center;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${DesignTokens.spacing.xl};
  margin-bottom: ${DesignTokens.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BookingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${DesignTokens.spacing.md};
  max-height: 400px;
  overflow-y: auto;
`;

const BookingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${DesignTokens.spacing.md};
  background: rgba(255, 255, 255, 0.08);
  border-radius: ${DesignTokens.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const BookingName = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  color: white;
`;

const BookingDay = styled.div`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
`;

const BookingCost = styled.div`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: #86EFAC;
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${DesignTokens.spacing.lg};
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const TotalSection = styled.div``;

const TotalLabel = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5);
`;

const TotalAmount = styled.div`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
`;

const FinalizeButton = styled(motion.button)`
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.xl};
  background: ${GlassmorphismTokens.gradient.blueCTA};
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  cursor: pointer;

  &:hover {
    background: ${GlassmorphismTokens.gradient.blueCTAHover};
    box-shadow: ${GlassmorphismTokens.shadow.blueCTA};
  }
`;

const safeNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

const parseCost = (costStr) => {
  if (!costStr) return 0;
  const str = String(costStr).toLowerCase();
  if (str.includes('free') || str.includes('no cost') || str.includes('included')) return 0;
  // Extract all numbers, average them (handles ranges like "€15-25" → 20)
  const nums = [];
  const re = /[\d][\d,.]*/g;
  let m;
  while ((m = re.exec(str)) !== null) {
    const n = parseFloat(m[0].replace(',', ''));
    if (!Number.isNaN(n)) nums.push(n);
  }
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const parseBudgetData = (itineraryData) => {
  const dailyItinerary = itineraryData.dailyItinerary || itineraryData.dayByDay || [];
  const activities = [];
  let activitiesTotal = 0;

  dailyItinerary.forEach((day, dayIdx) => {
    const dayNum = day.day || dayIdx + 1;
    const timeBlocks = [
      ...(day.morning || []),
      ...(day.afternoon || []),
      ...(day.evening || []),
      ...(day.activities || []),
    ];

    timeBlocks.forEach(activity => {
      if (typeof activity === 'object' && activity.estimatedCost) {
        const costStr = String(activity.estimatedCost);
        const cost = parseCost(costStr);
        activitiesTotal += cost;
        activities.push({
          name: activity.activity || activity.place || 'Activity',
          day: dayNum,
          cost: costStr,
          costNum: cost,
        });
      }
    });
  });

  activitiesTotal = safeNum(activitiesTotal);

  // Total is the sum of actual per-activity costs
  const totalBudget = safeNum(
    typeof itineraryData.estimatedBudget === 'number' && itineraryData.estimatedBudget > 0
      ? itineraryData.estimatedBudget
      : typeof itineraryData.estimatedCost === 'number' && itineraryData.estimatedCost > 0
        ? itineraryData.estimatedCost
        : activitiesTotal
  );

  // Build segments per day from actual per-activity data
  const dayTotals = {};
  activities.forEach(a => {
    dayTotals[a.day] = (dayTotals[a.day] || 0) + a.costNum;
  });
  const segments = Object.entries(dayTotals).map(([day, amount]) => ({
    label: `Day ${day}`,
    amount: safeNum(amount),
  }));

  // Fallback: if no per-day breakdown, show single "Activities" segment
  if (segments.length === 0 && totalBudget > 0) {
    segments.push({ label: 'Activities', amount: totalBudget });
  }

  return { activities, segments, totalBudget };
};

const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString()}`;
  }
};

const BudgetPlanner = ({ itineraryData, isOpen, onClose }) => {
  if (!itineraryData) return null;

  const { activities, segments, totalBudget } = parseBudgetData(itineraryData);
  const currency = itineraryData.currency || 'USD';

  const formattedTotal = typeof totalBudget === 'number'
    ? formatAmount(totalBudget, currency)
    : formatAmount(0, currency);

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <PlannerCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            <CloseButton onClick={onClose}>
              <XMarkIcon />
            </CloseButton>

            <Heading>Budget Planner</Heading>

            <TwoColumn>
              <BudgetDonutChart
                segments={segments}
                total={totalBudget}
                currency={currency}
              />

              <BookingList>
                {activities.length > 0 ? (
                  activities.map((item, idx) => (
                    <BookingItem key={idx}>
                      <div>
                        <BookingName>{item.name}</BookingName>
                        <BookingDay>Day {item.day}</BookingDay>
                      </div>
                      <BookingCost>{item.cost}</BookingCost>
                    </BookingItem>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px 0' }}>
                    No per-activity cost data available.
                  </div>
                )}
              </BookingList>
            </TwoColumn>

            <BottomBar>
              <TotalSection>
                <TotalLabel>Estimated Total</TotalLabel>
                <TotalAmount>{formattedTotal}</TotalAmount>
              </TotalSection>
              <FinalizeButton
                onClick={onClose}
                whileTap={{ scale: 0.95 }}
              >
                Done
              </FinalizeButton>
            </BottomBar>
          </PlannerCard>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default BudgetPlanner;
